/**
 * Murmuration Physics Utilities
 * 
 * Implements boids algorithm for flocking behaviour:
 * - Separation: Avoid collisions with nearby pills
 * - Alignment: Move in similar direction to nearby pills
 * - Cohesion: Stay near the center of the group
 */

import { PillState } from "@/hooks/useMurmuration";

export interface Vector2D {
  x: number;
  y: number;
}

/**
 * Calculate distance between two points
 */
export const distance = (a: Vector2D, b: Vector2D): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Normalize a vector to unit length
 */
export const normalize = (v: Vector2D): Vector2D => {
  const mag = Math.sqrt(v.x * v.x + v.y * v.y);
  if (mag === 0) return { x: 0, y: 0 };
  return { x: v.x / mag, y: v.y / mag };
};

/**
 * Limit vector magnitude to max value
 */
export const limit = (v: Vector2D, max: number): Vector2D => {
  const mag = Math.sqrt(v.x * v.x + v.y * v.y);
  if (mag > max) {
    const normalized = normalize(v);
    return { x: normalized.x * max, y: normalized.y * max };
  }
  return v;
};

/**
 * SEPARATION: Steer away from nearby pills to avoid collisions
 * Returns a force vector pushing away from crowded neighbors
 */
export const separation = (
  pill: PillState,
  pills: PillState[],
  separationDistance: number
): Vector2D => {
  const steer: Vector2D = { x: 0, y: 0 };
  let count = 0;

  pills.forEach((other) => {
    const d = distance(pill, other);
    
    // If pill is too close, add force pushing away
    if (d > 0 && d < separationDistance) {
      // Calculate difference vector (pointing away from other)
      const diff: Vector2D = {
        x: pill.x - other.x,
        y: pill.y - other.y,
      };
      
      // Weight by distance (closer = stronger force)
      const normalized = normalize(diff);
      steer.x += normalized.x / d;
      steer.y += normalized.y / d;
      count++;
    }
  });

  // Average the forces
  if (count > 0) {
    steer.x /= count;
    steer.y /= count;
  }

  return steer;
};

/**
 * ALIGNMENT: Move in the same direction as nearby pills
 * Returns a force vector aligning with the average velocity of neighbors
 */
export const alignment = (
  pill: PillState,
  pills: PillState[],
  neighborDistance: number
): Vector2D => {
  const avgVelocity: Vector2D = { x: 0, y: 0 };
  let count = 0;

  pills.forEach((other) => {
    const d = distance(pill, other);
    
    // Consider pills within neighbor distance
    if (d > 0 && d < neighborDistance) {
      avgVelocity.x += other.vx;
      avgVelocity.y += other.vy;
      count++;
    }
  });

  // Calculate average velocity
  if (count > 0) {
    avgVelocity.x /= count;
    avgVelocity.y /= count;
    
    // Return steering force toward average velocity
    return {
      x: avgVelocity.x - pill.vx,
      y: avgVelocity.y - pill.vy,
    };
  }

  return { x: 0, y: 0 };
};

/**
 * COHESION: Steer toward the average position of nearby pills
 * Returns a force vector pulling toward the center of the group
 */
export const cohesion = (
  pill: PillState,
  pills: PillState[],
  neighborDistance: number
): Vector2D => {
  const avgPosition: Vector2D = { x: 0, y: 0 };
  let count = 0;

  pills.forEach((other) => {
    const d = distance(pill, other);
    
    // Consider pills within neighbor distance
    if (d > 0 && d < neighborDistance) {
      avgPosition.x += other.x;
      avgPosition.y += other.y;
      count++;
    }
  });

  // Calculate average position (center of mass)
  if (count > 0) {
    avgPosition.x /= count;
    avgPosition.y /= count;
    
    // Return steering force toward center
    return {
      x: avgPosition.x - pill.x,
      y: avgPosition.y - pill.y,
    };
  }

  return { x: 0, y: 0 };
};

/**
 * CENTER PULL: Gentle force pulling pills toward the container center
 * Prevents pills from drifting too far from the group
 */
export const centerPull = (
  pill: PillState,
  centerX: number = 0,
  centerY: number = 0,
  strength: number = 0.001
): Vector2D => {
  return {
    x: (centerX - pill.x) * strength,
    y: (centerY - pill.y) * strength,
  };
};

/**
 * ELLIPSE BOUNDARY: Gentle force to keep pills within an elliptical boundary
 * Creates a horizontal "stage" for the constellation - only applies force when outside
 */
export function ellipseBoundaryForce(
  pill: PillState,
  centerX: number,
  centerY: number,
  radiusX: number,
  radiusY: number,
  strength: number
): Vector2D {
  // Calculate normalised distance from centre in ellipse space
  const dx = (pill.x - centerX) / radiusX;
  const dy = (pill.y - centerY) / radiusY;
  const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
  
  // Only apply force if outside the ellipse (distance > 1.0)
  if (distanceFromCenter > 1.0) {
    const force = (distanceFromCenter - 1.0) * strength;
    
    // Direction towards centre, accounting for ellipse shape
    const dirX = (centerX - pill.x) / radiusX;
    const dirY = (centerY - pill.y) / radiusY;
    const length = Math.sqrt(dirX * dirX + dirY * dirY);
    
    if (length > 0) {
      return {
        x: (dirX / length) * force,
        y: (dirY / length) * force
      };
    }
  }
  
  return { x: 0, y: 0 };
}

/**
 * Apply all boids forces to a pill and update its velocity
 */
export const applyBoidsForces = (
  pill: PillState,
  pills: PillState[],
  config: {
    separationDistance: number;
    alignmentDistance: number;
    cohesionDistance: number;
    separationWeight: number;
    alignmentWeight: number;
    cohesionWeight: number;
    centerPullStrength: number;
    centerX?: number;
    centerY?: number;
    ellipseBoundary?: {
      enabled: boolean;
      radiusX: number;
      radiusY: number;
      strength: number;
    };
    maxSpeed: number;
  }
): void => {
  // Calculate forces
  const separationForce = separation(pill, pills, config.separationDistance);
  const alignmentForce = alignment(pill, pills, config.alignmentDistance);
  const cohesionForce = cohesion(pill, pills, config.cohesionDistance);
  const centerForce = centerPull(pill, 0, 0, config.centerPullStrength);

  // Apply weighted forces to velocity
  pill.vx += separationForce.x * config.separationWeight;
  pill.vy += separationForce.y * config.separationWeight;
  
  pill.vx += alignmentForce.x * config.alignmentWeight;
  pill.vy += alignmentForce.y * config.alignmentWeight;
  
  pill.vx += cohesionForce.x * config.cohesionWeight;
  pill.vy += cohesionForce.y * config.cohesionWeight;
  
  // Apply ellipse boundary force (replaces centerPull when enabled)
  if (config.ellipseBoundary?.enabled && config.centerX !== undefined && config.centerY !== undefined) {
    const boundaryForce = ellipseBoundaryForce(
      pill,
      config.centerX,
      config.centerY,
      config.ellipseBoundary.radiusX,
      config.ellipseBoundary.radiusY,
      config.ellipseBoundary.strength
    );
    pill.vx += boundaryForce.x;
    pill.vy += boundaryForce.y;
  } else {
    // Fallback to simple centre pull if ellipse not enabled
    pill.vx += centerForce.x;
    pill.vy += centerForce.y;
  }

  // Add velocity damping to prevent overshooting and oscillations
  pill.vx *= 0.99;
  pill.vy *= 0.99;

  // Limit velocity to max speed
  const velocity = limit({ x: pill.vx, y: pill.vy }, config.maxSpeed);
  pill.vx = velocity.x;
  pill.vy = velocity.y;
};

/**
 * Simple spatial partitioning grid for performance optimization
 * Only checks pills in nearby grid cells instead of all pills
 */
export class SpatialGrid {
  private cellSize: number;
  private grid: Map<string, PillState[]>;

  constructor(cellSize: number = 100) {
    this.cellSize = cellSize;
    this.grid = new Map();
  }

  /**
   * Get grid cell key for a position
   */
  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);
    return `${cellX},${cellY}`;
  }

  /**
   * Clear the grid and rebuild with current pill positions
   */
  rebuild(pills: PillState[]): void {
    this.grid.clear();
    
    pills.forEach((pill) => {
      const key = this.getCellKey(pill.x, pill.y);
      if (!this.grid.has(key)) {
        this.grid.set(key, []);
      }
      this.grid.get(key)!.push(pill);
    });
  }

  /**
   * Get pills near a position (same cell + neighboring cells)
   */
  getNearby(x: number, y: number): PillState[] {
    const nearby: PillState[] = [];
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);

    // Check 3x3 grid around the cell
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${cellX + dx},${cellY + dy}`;
        const cellPills = this.grid.get(key);
        if (cellPills) {
          nearby.push(...cellPills);
        }
      }
    }

    return nearby;
  }
}
